import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppleIcon, GoogleIcon, HidePassword, OpenXLogo, ShowPassword } from '../../../assets';
import bgImage from '../../../assets/webp/SignInBGImage.webp';
import { useLoginWithEmailMutation } from '../../../redux/services/authApi';
import { ButtonComponent } from '../../components/button';
import styles from './style.module.css';
import { setTokenItem, TOKEN_KEY } from '../../../utils/helpers';
import FlatButtons from '../../../components/FlatButton/FlatButton';
import { useAppDispatch } from '../../../redux/hooks';
import { setAuthenticated } from '../../../redux/slices/authSlice';

export const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Change default redirect to home page instead of marketplace
  const from = (location.state as { from?: Location })?.from?.pathname || '/';
  const [loginUser, { isLoading }] = useLoginWithEmailMutation();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(null);

    try {
      const payload = await loginUser({ email, password }).unwrap();

      // Store tokens
      setTokenItem(TOKEN_KEY.AUTH_TOKEN, payload.access_token);
      setTokenItem(TOKEN_KEY.REFRESH_TOKEN, payload.refresh_token);
      
      // Update Redux auth state BEFORE navigation
      dispatch(setAuthenticated(true));

      // Small delay to ensure state updates before navigation
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
    } catch (err: any) {
      console.error('Failed to log in:', err);
      const errorMessage = err.data?.message || 'Invalid credentials. Please try again.';
      setApiError(errorMessage);
    }
  };

  return (
    <div className={styles.container}>
      <img className={styles.bgImg} src={bgImage} alt="Gallery Image" />

      <div className={styles.entryContainer}>
        <OpenXLogo /* className={styles.icon} */ />
        <div className={styles.headerTxt}>Sign into Your Account</div>
        <div className={styles.btnCont}>
          <div className={styles.signInBtn}>
            <GoogleIcon /> Sign In
          </div>
          <div className={styles.signInBtn}>
            <AppleIcon /> Sign In
          </div>
        </div>
        <div className={styles.orTxt}>OR</div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={styles.input}
              placeholder="johndoe@mail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.inputContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className={styles.input}
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <span className={styles.passwordToggle} onClick={togglePasswordVisibility}>
                {showPassword ? <HidePassword /> : <ShowPassword />}
              </span>
            </div>
          </div>
          <div
            className={styles.fPword}
            onClick={() => {
              navigate('/forgotPassword');
            }}
          >
            forgot password?
          </div>

          {apiError && <div className={styles.errorText}>{apiError}</div>}

          <ButtonComponent
            type="submit"
            buttonName={isLoading ? 'Signing In...' : 'Sign In'}
            disabled={isLoading}
            style={{
              marginTop: 40,
              border: 0,
              marginBottom: 36,
              width: "100%",
              maxWidth: "450px",
              height: '46px',
              opacity: isLoading ? 0.5 : 1,
            }}
          />
        </form>

        <div className={styles.signInCont}>
          Don't have an account?
          <Link to="/createAccount" style={{ textDecoration: 'none' }}>
            <div className={styles.signIn}>Sign up</div>
          </Link>
        </div>
      </div>
    </div>
  );
};